import { useMemo } from 'react';

import { api } from '../api/endpoints';
import { useProject } from './context';

type ProjectBound<T> = {
  [K in keyof T]: T[K] extends (projectId: string, ...args: infer A) => infer R ? (...args: A) => R : never;
};

const projectEndpoints = {
  leads: api.leads,
  lead: api.lead,
  updateLead: api.updateLead,
  appendLeadNote: api.appendLeadNote,
  members: api.members,
  regionStats: api.regionStats,
  postalCodeStats: api.postalCodeStats,
  geoReview: api.geoReview,
  geoReviewSummary: api.geoReviewSummary,
  startAddressChecks: api.startAddressChecks,
  addressChecks: api.addressChecks,
  acceptAddressChecks: api.acceptAddressChecks,
  correctGeo: api.correctGeo,
  confirmAddress: api.confirmAddress,
  askAssistant: api.askAssistant,
  assistantVoiceStatus: api.assistantVoiceStatus,
  startAssistantVoice: api.startAssistantVoice,
  assistantVoiceTalked: api.assistantVoiceTalked,
  responsibilities: api.responsibilities,
  resolveGeo: api.resolveGeo,
  projectAreas: api.projectAreas,
  dashboard: api.dashboard,
  teamOverview: api.teamOverview,
  settings: api.settings,
  saveSettings: api.saveSettings,
  queueNext: api.queueNext,
  queuePeek: api.queuePeek,
  setTelesalesTargets: api.setTelesalesTargets,
  setAppointmentGoal: api.setAppointmentGoal,
  setTelesalesScope: api.setTelesalesScope,
  appointmentConfirmation: api.appointmentConfirmation,
  partnerProspects: api.partnerProspects,
  createPartnerProspect: api.createPartnerProspect,
  importPartnerProspects: api.importPartnerProspects,
  updatePartnerProspect: api.updatePartnerProspect,
  partnerProspectEvents: api.partnerProspectEvents,
  enrichPartnerProspect: api.enrichPartnerProspect,
  partnerMailTemplates: api.partnerMailTemplates,
  bookingPages: api.bookingPages,
  upsertBookingPage: api.upsertBookingPage,
  bookings: api.bookings,
  draftPartnerMail: api.draftPartnerMail,
  previewPartnerMail: api.previewPartnerMail,
  sendPartnerMail: api.sendPartnerMail,
  startPartnerCall: api.startPartnerCall,
  callRecordingUrl: api.callRecordingUrl,
  enrichPartnerProspectsBatch: api.enrichPartnerProspectsBatch,
  addPartnerProspectEvent: api.addPartnerProspectEvent,
  buyerRequests: api.buyerRequests,
  updateBuyerRequest: api.updateBuyerRequest,
  buyerRequestMatches: api.buyerRequestMatches,
  leadBuyerMatches: api.leadBuyerMatches,
  leadAttachments: api.leadAttachments,
  uploadLeadAttachment: api.uploadLeadAttachment,
  updateLeadAttachment: api.updateLeadAttachment,
  deleteLeadAttachment: api.deleteLeadAttachment,
  leadAttachmentUrl: api.leadAttachmentUrl,
  updateAppointmentConfirmation: api.updateAppointmentConfirmation,
  queueStats: api.queueStats,
  focus: api.focus,
  lockLead: api.lockLead,
  unlockLead: api.unlockLead,
  activities: api.activities,
  sameNumberLeads: api.sameNumberLeads,
  leadOwners: api.leadOwners,
  leadObjects: api.leadObjects,
  updateLeadObject: api.updateLeadObject,
  splitLeadObject: api.splitLeadObject,
  getOwner: api.getOwner,
  setOwnerResidence: api.setOwnerResidence,
  leadCalls: api.leadCalls,
  startCall: api.startCall,
  activeCall: api.activeCall,
  call: api.call,
  endCall: api.endCall,
  documentTemplates: api.documentTemplates,
  createDocumentTemplate: api.createDocumentTemplate,
  updateDocumentTemplate: api.updateDocumentTemplate,
  leadDocuments: api.leadDocuments,
  createLeadDocument: api.createLeadDocument,
  createContractVariants: api.createContractVariants,
  updateDocumentApproval: api.updateDocumentApproval,
  markDocumentSigned: api.markDocumentSigned,
  contractCockpit: api.contractCockpit,
  setContractApprovalSetting: api.setContractApprovalSetting,
  leadDocument: api.leadDocument,
  updateLeadDocument: api.updateLeadDocument,
  deleteLeadDocument: api.deleteLeadDocument,
  finalizeLeadDocument: api.finalizeLeadDocument,
  requestSignature: api.requestSignature,
  cancelSignature: api.cancelSignature,
  duplicateLeadDocument: api.duplicateLeadDocument,
  brokers: api.brokers,
  placesAutocomplete: api.placesAutocomplete,
  placesAddress: api.placesAddress,
  placesSearch: api.placesSearch,
  placesValidate: api.placesValidate,
  documentRequest: api.documentRequest,
  saveDocumentRequest: api.saveDocumentRequest,
  uploadPowerOfAttorney: api.uploadPowerOfAttorney,
  sendDocumentRequest: api.sendDocumentRequest,
  createOwnerSheet: api.createOwnerSheet,
  documentRequestLetters: api.documentRequestLetters,
  uploadDocumentRequestFile: api.uploadDocumentRequestFile,
  updateDocumentRequestItem: api.updateDocumentRequestItem,
  propertyManagers: api.propertyManagers,
  createPropertyManager: api.createPropertyManager,
  updatePropertyManager: api.updatePropertyManager,
  exposes: api.exposes,
  createExpose: api.createExpose,
  deals: api.deals,
  createDeal: api.createDeal,
  updateDeal: api.updateDeal,
  closeDeal: api.closeDeal,
  cancelDeal: api.cancelDeal,
  ticketBookings: api.ticketBookings,
  assignBooking: api.assignBooking,
  inquiries: api.inquiries,
  inquiryObjects: api.inquiryObjects,
  assignInquiry: api.assignInquiry,
  expose: api.expose,
  updateExpose: api.updateExpose,
  deleteExpose: api.deleteExpose,
  submitExposeApproval: api.submitExposeApproval,
  approveExpose: api.approveExpose,
  publishExposeImmowelt: api.publishExposeImmowelt,
  exposeOpenImmoZipUrl: api.exposeOpenImmoZipUrl,
  uploadExposeImage: api.uploadExposeImage,
  updateExposeImage: api.updateExposeImage,
  deleteExposeImage: api.deleteExposeImage,
  editExposeImageWithAI: api.editExposeImageWithAI,
  suggestExposeTexts: api.suggestExposeTexts,
  startRecording: api.startRecording,
  startLiveAssist: api.startLiveAssist,
  stopLiveAssist: api.stopLiveAssist,
  conversationInsights: api.conversationInsights,
  qualitySummary: api.qualitySummary,
  createConversationReport: api.createConversationReport,
  callQuality: api.callQuality,
  stopRecording: api.stopRecording,
  setOutcome: api.setOutcome,
  contactState: api.contactState,
  followUps: api.followUps,
  createFollowUp: api.createFollowUp,
  parseFollowUp: api.parseFollowUp,
  completeFollowUp: api.completeFollowUp,
  requestFollowUp: api.requestFollowUp,
  cancelFollowUp: api.cancelFollowUp,
  appointmentTypes: api.appointmentTypes,
  createAppointmentType: api.createAppointmentType,
  suggestAppointment: api.suggestAppointment,
  createAppointment: api.createAppointment,
  appointment: api.appointment,
  rescheduleAppointment: api.rescheduleAppointment,
  cancelAppointment: api.cancelAppointment,
  phoneConfirmations: api.phoneConfirmations,
  appointmentReport: api.appointmentReport,
  createLead: api.createLead,
  callLeadSuggestions: api.callLeadSuggestions,
  assignCallToLead: api.assignCallToLead,
  saveAppointmentReport: api.saveAppointmentReport,
  submitAppointmentReport: api.submitAppointmentReport,
  dueAppointmentReports: api.dueAppointmentReports,
  recordPhoneConfirmation: api.recordPhoneConfirmation,
  setAppointmentStatus: api.setAppointmentStatus,
  assignAppointment: api.assignAppointment,
  appointmentInquiry: api.appointmentInquiry,
  handbackAppointment: api.handbackAppointment,
  resolveAppointmentHandback: api.resolveAppointmentHandback,
  appointmentHistory: api.appointmentHistory,
  regenerateBrief: api.regenerateBrief,
  sendAppointmentDossier: api.sendAppointmentDossier,
  leadAppointments: api.leadAppointments,
  fieldSalesDay: api.fieldSalesDay,
  fieldSalesLeads: api.fieldSalesLeads,
  calendar: api.calendar,
  availability: api.availability,
  calendarSync: api.calendarSync,
  retryCalendarSync: api.retryCalendarSync,
  aiReviews: api.aiReviews,
  acceptProposal: api.acceptProposal,
  rejectProposal: api.rejectProposal,
  acceptAction: api.acceptAction,
  rejectAction: api.rejectAction,
  leadConversationInsights: api.leadConversationInsights,
  callAnalysis: api.callAnalysis,
  resolveAnalysis: api.resolveAnalysis,
  tickets: api.tickets,
  ticket: api.ticket,
  createTicket: api.createTicket,
  updateTicket: api.updateTicket,
  addTicketComment: api.addTicketComment,
  editTicketComment: api.editTicketComment,
  ticketCategories: api.ticketCategories,
  createTicketCategory: api.createTicketCategory,
  updateTicketCategory: api.updateTicketCategory,
  leadEmails: api.leadEmails,
  sendLeadEmail: api.sendLeadEmail,
  leadEmailBounce: api.leadEmailBounce,
  clearLeadEmailBounce: api.clearLeadEmailBounce,
  fieldSalesRoute: api.fieldSalesRoute,
  recommendSlots: api.recommendSlots,
  mapLeads: api.mapLeads,
  mapAppointments: api.mapAppointments,
  mapAreas: api.mapAreas,
  mapAreaShapes: api.mapAreaShapes,
  privacyImpact: api.privacyImpact,
  privacyExport: api.privacyExport,
  privacyErase: api.privacyErase,
  privacyRequests: api.privacyRequests,
  privacyDownloadUrl: api.privacyDownloadUrl,
  priceList: api.priceList,
  priceListPdfUrl: api.priceListPdfUrl,
  myBrokerContract: api.myBrokerContract,
  brokerContract: api.brokerContract,
  setBrokerContract: api.setBrokerContract,
  appointmentMandate: api.appointmentMandate,
  reportMandate: api.reportMandate,
  mandates: api.mandates,
  cancelMandate: api.cancelMandate,
  invoices: api.invoices,
  invoice: api.invoice,
  createInvoice: api.createInvoice,
  updateInvoice: api.updateInvoice,
  discardInvoice: api.discardInvoice,
  issueInvoice: api.issueInvoice,
  markInvoicePaid: api.markInvoicePaid,
  unmarkInvoicePaid: api.unmarkInvoicePaid,
  cancelInvoice: api.cancelInvoice,
  invoicePdfUrl: api.invoicePdfUrl,
  removeMember: api.removeMember,
  addMember: api.addMember,
};

// useProjectApi bindet alle projektbezogenen Endpunkte an das aktive Projekt: Nach einem
// Projektwechsel arbeiten alle Requests automatisch im neuen Kontext. Die Berechtigung
// prüft weiterhin das Backend.
export function useProjectApi() {
  const { project } = useProject();
  return useMemo(() => {
    const bound = {} as Record<string, unknown>;
    for (const [name, fn] of Object.entries(projectEndpoints)) {
      bound[name] = (...args: unknown[]) => (fn as (id: string, ...rest: unknown[]) => unknown)(project.id, ...args);
    }
    return { projectId: project.id, ...(bound as ProjectBound<typeof projectEndpoints>) };
  }, [project.id]);
}

// Query-Keys enthalten immer die Projekt-ID, damit Caches projektgetrennt bleiben.
export const projectKeys = {
  all: (projectId: string) => ['projects', projectId] as const,
  leads: (projectId: string, filters: object) => ['projects', projectId, 'leads', filters] as const,
  regions: (projectId: string) => ['projects', projectId, 'regions'] as const,
  review: (projectId: string) => ['projects', projectId, 'geo-review'] as const,
  areas: (projectId: string) => ['projects', projectId, 'areas'] as const,
  members: (projectId: string) => ['projects', projectId, 'members'] as const,
  dashboard: (projectId: string) => ['projects', projectId, 'dashboard'] as const,
  team: (projectId: string) => ['projects', projectId, 'team-overview'] as const,
  priceList: (projectId: string, userId?: string) => ['projects', projectId, 'price-list', userId ?? ''] as const,
  brokerContract: (projectId: string, userId: string) => ['projects', projectId, 'broker-contracts', userId] as const,
  myBrokerContract: (projectId: string) => ['projects', projectId, 'broker-contracts', 'me'] as const,
  mandate: (projectId: string, appointmentId: string) => ['projects', projectId, 'mandate', appointmentId] as const,
  mandates: (projectId: string, brokerUserId: string) => ['projects', projectId, 'mandates', brokerUserId] as const,
  invoices: (projectId: string, status: string) => ['projects', projectId, 'invoices', 'list', status] as const,
  invoice: (projectId: string, invoiceId: string) => ['projects', projectId, 'invoices', invoiceId] as const,
  conversationInsights: (projectId: string, days: number) => ['projects', projectId, 'insights', 'conversations', days] as const,
  qualitySummary: (projectId: string, days: number) => ['projects', projectId, 'insights', 'quality', days] as const,
  settings: (projectId: string) => ['projects', projectId, 'settings'] as const,
  documentTemplates: (projectId: string, all = false) => ['projects', projectId, 'document-templates', all] as const,
  leadDocuments: (projectId: string, leadId: string) => ['projects', projectId, 'lead-documents', leadId] as const,
  contractCockpit: (projectId: string) => ['projects', projectId, 'contract-cockpit'] as const,
  document: (projectId: string, documentId: string) => ['projects', projectId, 'document', documentId] as const,
  brokers: (projectId: string) => ['projects', projectId, 'brokers'] as const,
  documentRequest: (projectId: string, leadId: string) => ['projects', projectId, 'document-request', leadId] as const,
  placesAutocomplete: (projectId: string, input: string, country = 'DE') => ['projects', projectId, 'places', 'autocomplete', country, input] as const,
  leadOwners: (projectId: string, leadId: string) => ['projects', projectId, 'lead-owners', leadId] as const,
  leadObjects: (projectId: string, leadId: string) => ['projects', projectId, 'lead-objects', leadId] as const,
  // unter 'lead-owners', damit das Speichern des Wohnorts auch die Eigentümer-Seite auffrischt
  owner: (projectId: string, ownerId: string) => ['projects', projectId, 'lead-owners', 'owner', ownerId] as const,
  placesSearch: (projectId: string, q: string) => ['projects', projectId, 'places', 'search', q] as const,
  propertyManagers: (projectId: string, q: string) => ['projects', projectId, 'property-managers', q] as const,
  exposes: (projectId: string, leadId: string) => ['projects', projectId, 'exposes', leadId] as const,
  deals: (projectId: string, leadId: string) => ['projects', projectId, 'deals', leadId] as const,
  inquiries: (projectId: string, q: object) => ['projects', projectId, 'inquiries', q] as const,
  ticketBookings: (projectId: string, ticketId: string) => ['projects', projectId, 'ticket-bookings', ticketId] as const,
  expose: (projectId: string, exposeId: string) => ['projects', projectId, 'expose', exposeId] as const,
  focus: (projectId: string, leadId: string) => ['projects', projectId, 'focus', leadId] as const,
  timeline: (projectId: string, leadId: string) => ['projects', projectId, 'timeline', leadId] as const,
  call: (projectId: string, callId: string) => ['projects', projectId, 'call', callId] as const,
  queueStats: (projectId: string) => ['projects', projectId, 'queue-stats'] as const,
  queuePeek: (projectId: string, exclude = '') => ['projects', projectId, 'queue-peek', exclude] as const,
  confirmation: (projectId: string, appointmentId: string) => ['projects', projectId, 'appointment-confirmation', appointmentId] as const,
  followUps: (projectId: string, filters: object) => ['projects', projectId, 'followups', filters] as const,
  appointmentTypes: (projectId: string) => ['projects', projectId, 'appointment-types'] as const,
  appointment: (projectId: string, appointmentId: string) => ['projects', projectId, 'appointment', appointmentId] as const,
  assistantVoice: (projectId: string) => ['projects', projectId, 'assistant-voice'] as const,
  appointmentHistory: (projectId: string, appointmentId: string) => ['projects', projectId, 'appointment-history', appointmentId] as const,
  day: (projectId: string, date: string, userId?: string) => ['projects', projectId, 'day', date, userId ?? 'me'] as const,
  brokerLeads: (projectId: string) => ['projects', projectId, 'broker-leads'] as const,
  phoneConfirmations: (projectId: string, state: 'open' | 'done') => ['projects', projectId, 'phone-confirmations', state] as const,
  appointmentReport: (projectId: string, appointmentId: string) => ['projects', projectId, 'appointment-report', appointmentId] as const,
  dueReports: (projectId: string) => ['projects', projectId, 'due-reports'] as const,
  partnerProspects: (projectId: string) => ['projects', projectId, 'partner-prospects'] as const,
  bookingPages: (projectId: string) => ['projects', projectId, 'booking-pages'] as const,
  bookings: (projectId: string) => ['projects', projectId, 'bookings'] as const,
  partnerMailTemplates: (projectId: string, prospectId: string) => ['projects', projectId, 'partner-prospects', prospectId, 'mail-templates'] as const,
  partnerProspectEvents: (projectId: string, prospectId: string) => ['projects', projectId, 'partner-prospects', prospectId, 'events'] as const,
  buyerRequests: (projectId: string, status: string) => ['projects', projectId, 'buyer-requests', status] as const,
  buyerMatches: (projectId: string, requestId: string) => ['projects', projectId, 'buyer-requests', 'matches', requestId] as const,
  leadBuyerMatches: (projectId: string, leadId: string) => ['projects', projectId, 'lead-buyer-matches', leadId] as const,
  leadAttachments: (projectId: string, leadId: string) => ['projects', projectId, 'lead-attachments', leadId] as const,
  callSuggestions: (projectId: string, callId: string) => ['projects', projectId, 'call-suggestions', callId] as const,
  calendar: (projectId: string, q: object) => ['projects', projectId, 'calendar', q] as const,
  aiReviews: (projectId: string, leadId?: string) => ['projects', projectId, 'ai-reviews', leadId ?? 'all'] as const,
  sync: (projectId: string) => ['projects', projectId, 'calendar-sync'] as const,
  tickets: (projectId: string, q: object) => ['projects', projectId, 'tickets', q] as const,
  ticket: (projectId: string, ticketId: string) => ['projects', projectId, 'ticket', ticketId] as const,
  ticketCategories: (projectId: string) => ['projects', projectId, 'ticket-categories'] as const,
  leadEmails: (projectId: string, leadId: string) => ['projects', projectId, 'lead-emails', leadId] as const,
  emailBounce: (projectId: string, leadId: string) => ['projects', projectId, 'email-bounce', leadId] as const,
  route: (projectId: string, date: string, userId?: string) => ['projects', projectId, 'route', date, userId ?? 'me'] as const,
  map: (projectId: string, layer: string, q: object) => ['projects', projectId, 'map', layer, q] as const,
  privacy: (projectId: string, leadId?: string) => ['projects', projectId, 'privacy', leadId ?? 'all'] as const,
};
